import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { ChevronLeft, ChevronRight, X, Heart, MessageCircle, Instagram } from 'lucide-react';

export default function GalleryLightbox({ images, captions = [] }) {
  const [idx, setIdx] = useState(null);
  const open = idx !== null;

  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'Escape') setIdx(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, next, prev]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {images.map((src, i) => (
          <button key={i} onClick={() => setIdx(i)} className="group relative overflow-hidden rounded-xl aspect-square block bg-neutral-900">
            <img src={src} alt="gallery" loading="lazy" className="w-full h-full object-cover transition duration-500 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-70 group-hover:opacity-100 transition" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
              <div className="w-11 h-11 rounded-full bg-orange-500/95 flex items-center justify-center shadow-lg"><Instagram className="w-5 h-5 text-white" /></div>
            </div>
            <div className="absolute bottom-2 left-2 right-2 flex items-center gap-3 text-white/90 text-[11px] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition">
              <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {(i + 1) * 128}</span>
              <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {(i + 1) * 12}</span>
            </div>
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(v) => !v && setIdx(null)}>
        <DialogContent className="max-w-5xl bg-black/95 border-orange-500/30 p-0 overflow-hidden">
          {open && (
            <div className="relative w-full h-[80vh] flex items-center justify-center">
              <img src={images[idx]} alt="expanded" className="max-w-full max-h-full object-contain" />
              <button onClick={() => setIdx(null)} className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 hover:bg-orange-500 flex items-center justify-center text-white transition"><X className="w-5 h-5" /></button>
              <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-orange-500 flex items-center justify-center text-white transition"><ChevronLeft className="w-6 h-6" /></button>
              <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-orange-500 flex items-center justify-center text-white transition"><ChevronRight className="w-6 h-6" /></button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-8 bg-orange-500' : 'w-1.5 bg-white/40'}`} />
                ))}
              </div>
              {captions[idx] && (
                <div className="absolute top-4 left-4 max-w-md bg-black/70 backdrop-blur px-4 py-2 rounded-xl text-white/85 text-sm">{captions[idx]}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
